/**
 * Token Refresh Watcher Hook
 * 
 * Proactively refreshes access token 5 minutes before expiry.
 * 
 * Features:
 * - Auto-refresh when token is close to expiry
 * - Cleans up interval on unmount
 * - Only runs when authenticated
 * - Handles errors gracefully (sign-out only when the server rejects the refresh token)
 * 
 * @example
 * ```tsx
 * // In App.tsx or main.tsx
 * function AppWrapper() {
 *   useTokenRefreshWatcher();
 *   return <App />;
 * }
 * ```
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../context/useAuth';
import { getAccessTokenExpiry } from '../storage/tokenStorage';

/**
 * How often to check if token needs refresh (30 seconds)
 */
const CHECK_INTERVAL_MS = 30_000;

/**
 * Refresh token 5 minutes before expiry (in milliseconds)
 * This matches the buffer in jwt.ts
 */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

export function useTokenRefreshWatcher() {
  const { isAuthenticated, refreshAccessToken } = useAuth();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only watch when authenticated
    if (!isAuthenticated) {
      return;
    }

    /**
     * Check if token needs refresh and refresh if needed
     */
    const checkAndRefresh = async () => {
      try {
        const expiry = await getAccessTokenExpiry();
        
        if (!expiry) {
          return;
        }

        // Calculate time until expiry
        const now = Math.floor(Date.now() / 1000); // Unix timestamp in seconds
        const timeUntilExpiry = (expiry - now) * 1000; // Convert to milliseconds

        // If token expires in less than 5 minutes, refresh
        if (timeUntilExpiry < REFRESH_BUFFER_MS) {
          await refreshAccessToken();
        }
      } catch (error) {
        console.error('[TokenRefreshWatcher] Refresh failed:', error);
        // Don't throw - let auth context handle the error
      }
    };

    // Initial check
    checkAndRefresh();

    // Set up interval to check periodically
    intervalRef.current = setInterval(checkAndRefresh, CHECK_INTERVAL_MS);

    // Cleanup on unmount or when auth status changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, refreshAccessToken]);
}
